import { DateRangePicker } from '@fullstory/components';
import { TimeRange } from '@fullstory/proto/fs/api/segment';
import { TimeRangeComparison } from '@fullstory/proto/fs/services/search-settings/visualizationsettings';
import { isValidSolrTime, parseSolrTime } from '@fullstory/search-converter/duration';
import { DurationUnit } from '@fullstory/search-types';
import { assertExhaustive } from '@fullstory/typeutils';
import { getAdjustedEndDate, isValidDate } from '@fullstory/util/date';
import moment from 'moment';
import {
  changeFromComparisonLabels,
  comparisonLabels,
} from '../components/MultiColumnTimePicker/utils';
import { dateRangeById, getDateRangeIdFromTimeRange } from '@fullstory/search-model';
import { DateRangeID } from '@fullstory/client-api';
import { intl } from '@fullstory/intl';

export type DateRanges = React.ComponentProps<typeof DateRangePicker>['ranges'];
export type DateRange = DateRanges[0];

type NonNullableObject<T> = { [K in keyof T]: NonNullable<T[K]> };
export interface DateRangeComparison extends Pick<TimeRangeComparison, 'canned'> {
  custom?: DateRange;
}

export function toDateRangeComparison(
  timeRangeComparison: TimeRangeComparison | null,
  now: Date,
): DateRangeComparison | null {
  if (!timeRangeComparison) {
    return null;
  }

  if (timeRangeComparison.custom) {
    return { custom: timeRangeToDateRange(timeRangeComparison.custom, now) };
  }

  return { canned: timeRangeComparison.canned };
}

export function toDateRangeComparisonDisplayText(
  timeRangeComparison: TimeRangeComparison | null,
  now: Date,
) {
  const dateRangeComparison = timeRangeComparison
    ? toDateRangeComparison(timeRangeComparison, now)
    : null;

  if (dateRangeComparison) {
    const compareText = getCompareText(dateRangeComparison, true);
    return intl.formatMessage(
      { defaultMessage: 'Compared to {compareText}', id: 'gySDLz' },
      { compareText: dateRangeComparison.custom ? compareText : compareText.toLocaleLowerCase() },
    );
  }

  return '';
}

export function toTimeRangeComparison(
  dateRangeComparison: DateRangeComparison | null,
  now: Date,
): TimeRangeComparison | null {
  if (!dateRangeComparison) {
    return null;
  }

  if (dateRangeComparison.custom) {
    return { custom: dateRangeToTimeRange(dateRangeComparison.custom) };
  }

  return { canned: dateRangeComparison.canned };
}

// Functions to convert between segment.TimeRange and DateRange,
// for compatability between the proto data and the date picker component.

/**
 * Converts a segment.TimeRange to a DateRange.
 * If the range startTime/endTime are relative solr timestamps,
 * they are converted to concrete dates relative to now.
 * @param range The TimeRange to convert
 * @param now The Date to use as 'now' for relative solr timestamps
 * @returns The equivalent DateRange to the given range
 */
export function timeRangeToDateRange(range: TimeRange, now: Date): NonNullableObject<DateRange> {
  return {
    start: timestampToDate(range.startTime, now),
    end: timestampToDate(range.endTime, now),
  };
}

/**
 * Converts a DateRange to a segment.TimeRange.
 * @param range The DateRange to convert
 * @returns The equivalent TimeRange to the given range
 */
export function dateRangeToTimeRange(range: DateRange): TimeRange {
  return {
    startTime: range.start?.toISOString() || '',
    endTime: range.end?.toISOString() || '',
  };
}

/**
 * Helper function to convert the timestamp into a Date.
 * For relative solr timestamps, returns the date relative to now.
 * For RFC 3339 timestamps, returns a date object corresponding to the timestamp.
 */
export function timestampToDate(timestamp: string, now: Date): Date {
  if (!isValidSolrTime(timestamp)) {
    if (timestamp === '') {
      return new Date();
    }
    // If not a valid solr time, the timestamp is an RFC 3339 timestamp and can be passed into the Date constructor
    return new Date(timestamp);
  }
  const duration = parseSolrTime(timestamp);
  const result = moment(now).startOf(duration.unit === DurationUnit.hour ? 'hour' : 'day');
  if (!duration.scalar) {
    return result.toDate();
  }

  switch (duration.unit) {
    case DurationUnit.milli: {
      result.add(duration.scalar, 'ms');
      break;
    }
    case DurationUnit.second: {
      result.add(duration.scalar, 's');
      break;
    }
    case DurationUnit.minute: {
      result.add(duration.scalar, 'm');
      break;
    }
    case DurationUnit.hour: {
      result.add(duration.scalar, 'h');
      break;
    }
    case DurationUnit.day: {
      result.add(duration.scalar, 'd');
      break;
    }
    default:
      assertExhaustive(duration.unit);
  }

  return result.toDate();
}

/**
 *
 * @param start
 * @param end
 * @returns DST offset between 2 dates in milliseconds
 */
const getDstAdjustment = (start: Date, end: Date) => {
  // utcOffset returns minutes
  // ex. for EST, it will return -240 or -300 depending on DST
  const startUtcOffset = moment(start).utcOffset();
  const endUtcOffset = moment(end).utcOffset();
  // convert minutes to milliseconds
  return (endUtcOffset - startUtcOffset) * 60000;
};

/**
 * Generates a relative DateRange given some DateRange and a TimeRangeComparison
 * @param range The date range the comparison range is relative to
 * @param comparison The DateRangeComparison that determines how the new range
 * should be calculated relative to the primary range.
 *
 * @return The new DateRange relative to the primary range.
 */
export function getComparisonDateRange(
  range: DateRange,
  comparison: DateRangeComparison,
): DateRange {
  if (comparison.custom) {
    return comparison.custom;
  }

  if (!range.start || !range.end) {
    return { start: null, end: null };
  }

  // DST adjustment on original time range
  const dstAdjustmentOriginalRange = getDstAdjustment(range.start, range.end);
  let start = moment(range.start);
  // original time range period in ms, accounting for DST
  const period = moment(range.end).diff(start) + dstAdjustmentOriginalRange;

  switch (comparison.canned) {
    case TimeRangeComparison.ComparisonDateRangeID.PREVIOUS_PERIOD: {
      // Start should be the original start minus the period
      start.subtract(period, 'milliseconds');
      // subtracting ms does not adjust for DST, need to do this again in case the
      // resulting previous range crosses DST, but the original range does not.
      start.subtract(getDstAdjustment(range.start, start.toDate()), 'milliseconds');
      break;
    }
    case TimeRangeComparison.ComparisonDateRangeID.PREVIOUS_DAY: {
      // Start is one day before original
      // DST safe
      start.subtract(1, 'days');
      break;
    }
    case TimeRangeComparison.ComparisonDateRangeID.PREVIOUS_WEEK: {
      // Start is seven days before original
      // DST Safe
      start.subtract(7, 'days');
      break;
    }
    case TimeRangeComparison.ComparisonDateRangeID.PREVIOUS_MONTH: {
      // Start is the same day of the previous month.
      // DST Safe
      start = moment(adjustMonth(start.toDate(), 1));
      break;
    }
    case TimeRangeComparison.ComparisonDateRangeID.PREVIOUS_QUARTER: {
      // Start is the same day of the third previous month.
      // DST safe
      start = moment(adjustMonth(start.toDate(), 3));
      break;
    }
    case TimeRangeComparison.ComparisonDateRangeID.PREVIOUS_YEAR: {
      // Start is the same day of the previous year.
      // No need to use adjustMonth because month is the same
      // DST safe
      start.subtract(12, 'months');
      break;
    }
    case undefined: {
      throw new Error('invalid comparison: missing both canned and custom range');
    }
    default:
      assertExhaustive(comparison.canned);
  }

  const end = moment(start).add(period, 'milliseconds');
  // if the newly calculated range crosses DST, we need to adjust for that too
  const dstAdjustmentRange = getDstAdjustment(start.toDate(), end.toDate());

  // since start is calculated with momentjs, we don't need to adjust it for DST
  return { start: start.toDate(), end: end.subtract(dstAdjustmentRange, 'milliseconds').toDate() };
}

export function getComparisonTimeRange(
  range: TimeRange,
  comparison: TimeRangeComparison,
  now: Date,
): TimeRange {
  const dateRange = timeRangeToDateRange(range, now);
  const dateRangeComparison = toDateRangeComparison(comparison, now);
  const comparisonDateRange = getComparisonDateRange(dateRange, dateRangeComparison!);
  return dateRangeToTimeRange(comparisonDateRange);
}

/**
 * Returns a new date that is the given date, minus the number of months.
 * Adjusts to the last day of the new month if the current date is on a day
 * which doesn't exist in the new month (e.g., March 31 -> Feb 28)
 */
function adjustMonth(d: Date, months: number): Date {
  const result = moment(d);
  // DST safe
  result.subtract(months, 'months');
  // moment.month returns a number 0 - 11. Add 12 and mod to adjust for negative
  const expectedMonth = (moment(d).month() + 12 - months) % 12;

  // in this case, the current date doesn't exist in that month,
  // so it's flipped over to next month, eg, it got set to feburary 31st,
  // which JS interprets as march 2nd
  if (result.month() !== expectedMonth) {
    result.endOf('month');
  }
  return result.toDate();
}

export function getCompareText(comparison: DateRangeComparison, short: boolean = false): string {
  if (comparison.canned !== undefined) {
    return comparisonLabels[comparison.canned];
  }
  return dateRangeToString(comparison.custom, short);
}

export function getChangeFromCompareText(comparison: DateRangeComparison): string {
  if (comparison.canned !== undefined) {
    return changeFromComparisonLabels[comparison.canned];
  }
  return changeFromDateRangeToString(comparison.custom);
}

export function changeFromDateRangeToString(range: DateRange | undefined): string {
  if (!range?.start || !range.end || !isValidDate(range.start) || !isValidDate(range.end)) {
    return intl.formatMessage({ defaultMessage: 'Change from custom time range:', id: '2IJ4Df' });
  }
  const adjustedRangeEnd = getAdjustedEndDate(range.end).toDate();
  return intl.formatMessage(
    {
      defaultMessage: 'Change from {start, date, ::MMMdy} - {end, date, ::MMMdy}: ',
      id: 'kle1wh',
      description: 'A date range',
    },
    { start: range.start, end: adjustedRangeEnd },
  );
}

export function dateRangeToString(range: DateRange | undefined, short: boolean = false): string {
  if (!range?.start || !range.end || !isValidDate(range.start) || !isValidDate(range.end)) {
    return intl.formatMessage({ defaultMessage: 'custom time range', id: 'rPUNAG' });
  }
  const adjustedRangeEnd = getAdjustedEndDate(range.end).toDate();
  return short
    ? intl.formatMessage(
        {
          defaultMessage: '{start, date, ::Mdy} - {end, date, ::Mdy}',
          id: 'ks3OoO',
          description: 'A date range',
        },
        { start: range.start, end: adjustedRangeEnd },
      )
    : intl.formatMessage(
        {
          defaultMessage: '{start, date, ::MMMdy} - {end, date, ::MMMdy}',
          id: '+dxgHf',
          description: 'A date range',
        },
        { start: range.start, end: adjustedRangeEnd },
      );
}

export function getDateDisplayString(startDate: string, endDate: string): string {
  const dateRangeId = getDateRangeIdFromTimeRange({
    Start: startDate,
    End: endDate,
  });

  let displayString = '';
  switch (dateRangeId) {
    case DateRangeID.Custom:
      displayString = intl.formatMessage(
        {
          defaultMessage: '{start, date, ::Mdy} - {end, date, ::Mdy}',
          id: 'ks3OoO',
          description: 'A date range',
        },
        { start: new Date(startDate), end: getAdjustedEndDate(new Date(endDate)).toDate() },
      );
      break;
    case DateRangeID.Since:
      displayString = intl.formatMessage(
        {
          defaultMessage: 'Since {date, date, ::Mdy}',
          id: 'F2kPYv',
          description: 'Represents a time range from a date until now',
        },
        { date: new Date(startDate) },
      );
      break;
    default:
      displayString = dateRangeById[dateRangeId].name;
      break;
  }
  return displayString;
}
